import { openSqlite } from "@skaile/workspaces/core/logging";
import type { LogEntry, LogQuery } from "@skaile/workspaces/types";
import { rowToEntry, type SqliteLogRow } from "./sqlite-row.ts";

export interface LocalQueryResult {
  entries: LogEntry[];
  nextCursor?: string;
}

/**
 * Open a SQLite log DB read-only and run a filtered SELECT mirroring the
 * `LogQuery` shape used by `SessionLogsService` in platform. Returns up to
 * `q.limit` (default 200) entries in chronological order with an optional
 * `nextCursor` for backward pagination.
 *
 * Uses the runtime's native SQLite driver — `bun:sqlite` under Bun,
 * `node:sqlite` under Node — via {@link openSqlite}.
 */
export function localQuery(
  dbPath: string,
  q: Omit<LogQuery, "sessionId"> & { sessionId: string },
): LocalQueryResult {
  const db = openSqlite(dbPath, { readonly: true });
  try {
    db.exec("PRAGMA query_only=1;");
    const limit = q.limit ?? 200;
    const fetchN = limit + 1; // overshoot by one to detect more-available
    const { sql, params } = buildSelect({ ...q, limit: fetchN });
    const rows = db.prepare(sql).all<SqliteLogRow>(params);

    const all = rows.map(rowToEntry);
    const hasMore = all.length > limit;
    const trimmed = hasMore ? all.slice(0, limit) : all;
    // Reverse to chronological for display (SELECT is DESC for cursor pagination).
    const chronological = trimmed.slice().reverse();
    const nextCursor = hasMore ? trimmed[trimmed.length - 1]!.id : undefined;
    return { entries: chronological, ...(nextCursor ? { nextCursor } : {}) };
  } finally {
    db.close();
  }
}

interface BuiltSelect {
  sql: string;
  params: Record<string, unknown>;
}

function buildSelect(q: Omit<LogQuery, "sessionId"> & { sessionId: string }): BuiltSelect {
  const where: string[] = ["session_id = $sid"];
  const params: Record<string, unknown> = { $sid: q.sessionId };

  if (q.cursor) {
    where.push("id < $cursor");
    params.$cursor = q.cursor;
  }
  if (q.source?.kind) {
    where.push("kind = $kind");
    params.$kind = q.source.kind;
  }
  if (q.source?.subkind) {
    where.push("subkind = $sub");
    params.$sub = q.source.subkind;
  }
  if (q.source?.instance) {
    where.push("instance = $inst");
    params.$inst = q.source.instance;
  }
  if (q.levels && q.levels.length > 0) {
    const placeholders = q.levels.map((_, i) => `$lvl${i}`).join(", ");
    where.push(`level IN (${placeholders})`);
    q.levels.forEach((lvl, i) => {
      params[`$lvl${i}`] = lvl;
    });
  }
  if (q.since) {
    where.push("timestamp >= $since");
    params.$since = q.since;
  }
  if (q.until) {
    where.push("timestamp <= $until");
    params.$until = q.until;
  }
  if (q.search) {
    where.push("message LIKE $search");
    params.$search = `%${q.search}%`;
  }

  const limit = q.limit ?? 200;
  params.$limit = limit;

  const sql = `
    SELECT id, timestamp, session_id, kind, subkind, instance, level, message, data_json, error_json
    FROM log_entries
    WHERE ${where.join(" AND ")}
    ORDER BY id DESC
    LIMIT $limit
  `;
  return { sql, params };
}
